// Commercial page support for the Visual Sitemap: what holds each page up in the four-tier
// model (home → hub/pillar → article). Pure functions over the /sitemap/tree payload.

// A post linked from this many posts of its own cluster is that cluster's pillar.
export const PILLAR_MIN_LINKS = 2;
// Supporting posts a commercial page needs (with hub and pillar) to count as fully supported.
export const STRONG_POSTS = 3;

export function normUrl(u) {
  if (!u) return "";
  try {
    const x = new URL(u);
    return (x.host.replace(/^www\./, "") + x.pathname.replace(/\/+$/, "")).toLowerCase();
  } catch { return String(u).replace(/\/+$/, "").toLowerCase(); }
}

export function pathOf(u) {
  try { return new URL(u).pathname.replace(/\/+$/, "") || "/"; } catch { return ""; }
}

const STOPWORDS = new Set([
  "the", "a", "an", "of", "for", "and", "or", "to", "in", "on", "with", "is", "are", "how", "what", "why",
  "your", "you", "this", "that", "best", "dan", "yang", "untuk", "dengan", "dari", "atau", "dalam", "pada",
  "cara", "apa", "ini", "itu", "anda", "kami",
]);
const words = (text) => (text || "").toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3 && !STOPWORDS.has(w));
const LOCALE_SEG = /^[a-z]{2}(-[a-z]{2})?$/;

function addTo(map, k, v) {
  if (!map.has(k)) map.set(k, new Set());
  map.get(k).add(v);
}

// What holds each commercial page up, in the four-tier model (home → hub/pillar → article):
// its hub (the page one URL level up), the pillar of its topic cluster, and the posts that
// link to it. A pillar is the post its own cluster links to most; the cluster a page belongs
// to is the one whose posts link to it most, else the closest by topic words.
export function analyzeSupport(data, clusterLabels) {
  const { articles = [], pages = [], internal_links = [] } = data;
  const artByUrl = new Map(articles.map((a) => [normUrl(a.wp_url), a]));
  const pageByUrl = new Map(pages.map((p) => [normUrl(p.url), p]));
  const outLinks = new Map();
  const inLinks = new Map();
  for (const l of internal_links) {
    const s = normUrl(l.source_url);
    const t = normUrl(l.target_url);
    if (!s || !t || s === t) continue;
    addTo(outLinks, s, t);
    addTo(inLinks, t, s);
  }

  // Topic clusters when articles have them; WP categories otherwise.
  const hasClusterIds = articles.some((a) => a.cluster_id);
  const groups = new Map();
  const groupOf = new Map();
  for (const a of articles) {
    let id, kind, label;
    if (hasClusterIds && a.cluster_id) {
      id = `cluster-${a.cluster_id}`;
      kind = "cluster";
      label = clusterLabels.get(String(a.cluster_id)) || a.primary_topic || `Cluster ${a.cluster_id}`;
    } else {
      label = (a.categories && a.categories[0]) || "Uncategorized";
      id = `cat-${label.replace(/\W+/g, "-").toLowerCase()}`;
      kind = "category";
    }
    if (!groups.has(id)) groups.set(id, { id, kind, label, posts: [] });
    groups.get(id).posts.push(a);
    groupOf.set(normUrl(a.wp_url), groups.get(id));
  }

  for (const g of groups.values()) {
    const own = new Set(g.posts.map((a) => normUrl(a.wp_url)));
    let best = null;
    let bestN = 0;
    for (const a of g.posts) {
      const n = [...(inLinks.get(normUrl(a.wp_url)) || [])].filter((s) => own.has(s)).length;
      if (n > bestN) { best = a; bestN = n; }
    }
    g.topPost = best;
    g.pillar = bestN >= PILLAR_MIN_LINKS ? best : null;
    g.pillarLinks = bestN;
    g.terms = new Set([g.label, ...g.posts.map((a) => a.primary_topic)].flatMap(words));
  }
  // A word in many clusters (the brand, the industry) can't say which cluster a page is about.
  const df = new Map();
  groups.forEach((g) => g.terms.forEach((w) => df.set(w, (df.get(w) || 0) + 1)));
  const tooCommon = (w) => df.get(w) >= Math.max(3, 0.3 * groups.size);

  const support = new Map();
  const hubIds = new Set();
  for (const p of pages) {
    const u = normUrl(p.url);
    const linkers = [...(inLinks.get(u) || [])].map((s) => artByUrl.get(s)).filter(Boolean);

    const path = pathOf(p.url);
    const segs = path.split("/").filter(Boolean);
    const topLevel = segs.length <= 1 || (segs.length === 2 && LOCALE_SEG.test(segs[0]));
    let hub = { status: "top" };
    if (!topLevel) {
      const hubPath = "/" + segs.slice(0, -1).join("/");
      let hubPage = null;
      try {
        const x = new URL(p.url);
        x.pathname = hubPath;
        hubPage = pageByUrl.get(normUrl(x.href)) || null;
      } catch { /* relative url: no hub lookup */ }
      hub = hubPage ? { status: "ok", path: hubPath, page: hubPage } : { status: "missing", path: hubPath };
      if (hubPage) hubIds.add(String(hubPage.id));
    }

    const counts = new Map();
    linkers.forEach((a) => {
      const g = groupOf.get(normUrl(a.wp_url));
      if (g) counts.set(g, (counts.get(g) || 0) + 1);
    });
    let cluster = null;
    let matchedBy = null;
    if (counts.size) {
      cluster = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
      matchedBy = "links";
    } else {
      const pw = new Set(words(`${p.title} ${path.replace(/[-/_]/g, " ")}`));
      let best = 0;
      for (const g of groups.values()) {
        const score = [...pw].filter((w) => g.terms.has(w) && !tooCommon(w)).reduce((sum, w) => sum + 1 / df.get(w), 0);
        if (score > best) { best = score; cluster = g; }
      }
      if (cluster) matchedBy = "topic";
    }
    const pillar = cluster?.pillar || null;
    const pillarLinks = !!pillar && linkers.includes(pillar);
    const linked = new Set(linkers);
    const candidates = cluster ? cluster.posts.filter((a) => !linked.has(a)) : [];
    const hubOk = hub.status !== "missing";
    const level = linkers.length === 0 ? "none" : hubOk && pillarLinks && linkers.length >= STRONG_POSTS ? "strong" : "partial";
    support.set(String(p.id), { linkers, hub, cluster, matchedBy, pillar, pillarLinks, candidates, level });
  }

  const postTargets = new Map(
    articles.map((a) => [a.wp_post_id, [...(outLinks.get(normUrl(a.wp_url)) || [])].map((t) => pageByUrl.get(t)).filter(Boolean)]),
  );
  return { groups, groupOf, support, hubIds, postTargets, pageByUrl, artByUrl };
}

// ── Sitemap plan: support gaps → ranked tasks ─────────────────────────────────

// Legal, account, cart and archive pages are on every site and never need blog support.
const UTILITY = /^(privacy|privacy-policy|terms|terms-of-service|cookies?|cookie-policy|legal|disclaimer|contact|contact-us|login|signin|sign-in|register|signup|sign-up|account|my-account|cart|checkout|thank-you|thanks|404|search|tag|tags|author|sitemap|kebijakan-privasi|syarat-dan-ketentuan|kontak|hubungi-kami|masuk|daftar|keranjang)$/;

export function isUtilityPage(url) {
  return pathOf(url).split("/").filter(Boolean).some((seg) => UTILITY.test(seg));
}

export const PLAN_KINDS = {
  old:          { label: "Retire old copy",      rank: 0, action: "technical" },
  pillar_link:  { label: "Link from pillar",     rank: 1, action: "internal_links" },
  hub:          { label: "Create hub page",      rank: 2, action: "write_new" },
  links:        { label: "Add supporting links", rank: 3, action: "internal_links" },
  pillar_write: { label: "Write pillar",         rank: 4, action: "write_new" },
  write:        { label: "Write support content", rank: 5, action: "write_new" },
};
const TIER_RANK = { high: 0, medium: 1, low: 2, unscored: 3 };
const TASK_LIST = 8;

// Lead value of each commercial page: the best lead tier among search topics whose key page it
// is (query_topics.lead_tier, buyer fit and awareness). Impressions only ride along as feedback.
function pageValues(pages, topics) {
  const byUrl = new Map();
  for (const t of topics || []) {
    const u = normUrl(t.matched_key_page);
    if (!u) continue;
    const cur = byUrl.get(u);
    const tier = t.lead_tier || "unscored";
    if (!cur || TIER_RANK[tier] < TIER_RANK[cur.tier]) byUrl.set(u, { tier, topic: t.topic, impressions: t.impressions || 0 });
  }
  return new Map(pages.map((p) => [String(p.id), byUrl.get(normUrl(p.url)) || { tier: "unscored", topic: null, impressions: 0 }]));
}

const bestTier = (vals) => vals.reduce((best, v) => (TIER_RANK[v.tier] < TIER_RANK[best] ? v.tier : best), "unscored");
const list = (rows) => rows.slice(0, TASK_LIST).map((r) => `- ${r}`).join("\n") + (rows.length > TASK_LIST ? `\n- …and ${rows.length - TASK_LIST} more` : "");
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Turns the sitemap's support analysis into ranked task recommendations. One fix that helps many
 * pages (a hub, a pillar) is one task listing them all; per-page tasks only for what's per page.
 * Ranked by the lead value of the pages helped, then by how cheap and far-reaching the fix is
 * (retire old copies, link from an existing pillar, add a hub, add post links, write), then by
 * pages helped. Search volume never ranks.
 *
 * data: GET /sitemap/tree. ax: analyzeSupport(data). topics: query topics (may be empty).
 */
export function buildSitemapPlan(data, ax, topics = []) {
  const pages = (data.pages || []).filter((p) => !isUtilityPage(p.url));
  const value = pageValues(pages, topics);
  const scored = [...value.values()].some((v) => v.tier !== "unscored");
  const items = [];
  const name = (p) => p.title || pathOf(p.url);
  const line = (p) => `${name(p)} (${pathOf(p.url)})`;

  const push = (kind, id, { label, why, helped, target, description, priority }) => {
    const tier = bestTier(helped.map((p) => value.get(String(p.id))));
    // Without lead scores, reach decides: a fix for 3+ pages beats a one-page fix.
    const prio = priority || (scored
      ? { high: "high", medium: "medium", low: "low", unscored: "low" }[tier]
      : helped.length >= 3 ? "medium" : "low");
    items.push({
      id: `${kind}:${id}`,
      kind,
      label,
      why,
      tier,
      helped: helped.map((p) => ({ id: p.id, title: name(p), url: p.url })),
      rank: [TIER_RANK[tier], PLAN_KINDS[kind].rank, -helped.length],
      task: {
        title: label,
        action_type: PLAN_KINDS[kind].action,
        priority: prio,
        target_url: target || null,
        labels: ["sitemap", kind.replace("_", "-")],
        queries: [],
        description,
      },
    });
  };

  // Old copies still live: split rankings and show outdated offers, whatever the page's value.
  for (const r of data.review_pages || []) {
    items.push({
      id: `old:${normUrl(r.url)}`,
      kind: "old",
      label: `Retire old copy ${pathOf(r.url)}`,
      why: r.reasons.join(" · "),
      tier: r.linked_from_home ? "high" : "medium",
      helped: [],
      rank: [r.linked_from_home ? 0 : 1, PLAN_KINDS.old.rank, 0],
      task: {
        title: `Retire old copy ${pathOf(r.url)}`,
        action_type: "technical",
        priority: r.linked_from_home ? "urgent" : "high",
        target_url: r.url,
        labels: ["sitemap", "old-copy"],
        queries: [],
        description: [`Looks like an old version that's still live: ${r.reasons.join(", ")}.`, r.action].filter(Boolean).join("\n\n"),
      },
    });
  }

  const group = (keyOf) => {
    const m = new Map();
    for (const p of pages) {
      const s = ax.support.get(String(p.id));
      const k = s && keyOf(s, p);
      if (!k) continue;
      if (!m.has(k)) m.set(k, { s, pages: [] });
      m.get(k).pages.push(p);
    }
    return m;
  };

  // An existing pillar that doesn't link to its cluster's commercial pages: the cheapest fix.
  for (const [, { s, pages: ps }] of group((s) => (s.pillar && !s.pillarLinks ? `${s.pillar.wp_post_id}` : null))) {
    push("pillar_link", s.pillar.wp_post_id, {
      label: `Link pillar "${s.pillar.title}" to ${plural(ps.length, "commercial page")}`,
      why: `Pillar of "${s.cluster.label}". A pillar link tells readers and AI which page sells what the guide explains.`,
      helped: ps,
      target: s.pillar.wp_url,
      description: [
        `"${s.pillar.title}" is the pillar of the "${s.cluster.label}" cluster but doesn't link to these commercial pages. Add a contextual link to each:`,
        list(ps.map(line)),
      ].join("\n\n"),
    });
  }

  for (const [path, { pages: ps }] of group((s) => (s.hub.status === "missing" ? s.hub.path : null))) {
    push("hub", path, {
      label: `Create hub page ${path}`,
      why: `${plural(ps.length, "page")} under ${path} have no parent page to link them from one level up.`,
      helped: ps,
      target: null,
      description: [
        `Nothing lives at ${path}. Create a hub page there that introduces the section and links to every page in it, and link the hub from the main menu or homepage:`,
        list(ps.map(line)),
      ].join("\n\n"),
    });
  }

  for (const [, { s, pages: ps }] of group((s) => (s.cluster && !s.pillar ? s.cluster.id : null))) {
    const g = s.cluster;
    // The post its siblings already link to most is the natural one to grow into the pillar.
    const seed = g.topPost;
    push("pillar_write", g.id, {
      label: `Write a pillar for "${g.label}"`,
      why: `${plural(g.posts.length, "post")} but no pillar, so ${plural(ps.length, "commercial page")} have no guide pointing at them.`,
      helped: ps,
      target: null,
      description: [
        `The "${g.label}" cluster has no pillar (no post linked from ${PILLAR_MIN_LINKS}+ of its siblings). Write one guide that covers the topic end to end, links to every post in the cluster, and links to these commercial pages:`,
        list(ps.map(line)),
        seed ? `Or grow "${seed.title}" into it: it's already the most linked post in the cluster.` : "",
      ].filter(Boolean).join("\n\n"),
    });
  }

  for (const p of pages) {
    const s = ax.support.get(String(p.id));
    if (!s || s.linkers.length >= STRONG_POSTS) continue;
    const v = value.get(String(p.id));
    const topicNote = v.topic ? `Key page for the search topic "${v.topic}" (${v.impressions} impressions as feedback).` : "";
    if (!s.cluster) {
      push("write", p.id, {
        label: `Write supporting content for ${name(p)}`,
        why: "No topic cluster on the blog matches this page.",
        helped: [p],
        target: p.url,
        description: [
          `No blog cluster covers what ${line(p)} sells, so nothing explains it or links to it. Write a pillar guide on its topic, plus 2-3 articles answering buyers' questions, each linking here.`,
          topicNote,
        ].filter(Boolean).join("\n\n"),
      });
      continue;
    }
    const need = STRONG_POSTS - s.linkers.length;
    const from = s.candidates.filter((a) => a !== s.pillar).slice(0, Math.max(need + 2, TASK_LIST));
    if (!from.length) continue;
    push("links", p.id, {
      label: `Add ${plural(need, "supporting link")} to ${name(p)}`,
      why: `${plural(s.linkers.length, "post")} link here; posts in "${s.cluster.label}" that could.`,
      helped: [p],
      target: p.url,
      description: [
        `${line(p)} has ${plural(s.linkers.length, "supporting post")}; aim for ${STRONG_POSTS}+. Add a contextual link here from posts in "${s.cluster.label}":`,
        list(from.map((a) => `${a.title} (${pathOf(a.wp_url)})`)),
        topicNote,
      ].filter(Boolean).join("\n\n"),
    });
  }

  const cmp = (a, b) => {
    for (let i = 0; i < a.rank.length; i++) {
      if (a.rank[i] !== b.rank[i]) return a.rank[i] < b.rank[i] ? -1 : 1;
    }
    return 0;
  };
  const supportable = pages.map((p) => ax.support.get(String(p.id))).filter(Boolean);
  return {
    items: items.sort(cmp),
    scored,
    insights: {
      pages: pages.length,
      utility: (data.pages || []).length - pages.length,
      none: supportable.filter((s) => s.level === "none").length,
      strong: supportable.filter((s) => s.level === "strong").length,
      hubsMissing: new Set(supportable.filter((s) => s.hub.status === "missing").map((s) => s.hub.path)).size,
      clustersNoPillar: [...ax.groups.values()].filter((g) => !g.pillar).length,
      clusters: ax.groups.size,
      old: (data.review_pages || []).length,
    },
  };
}
